import { t as uiText } from '../../i18n';
import React, { memo, useCallback, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { writeBatch, doc } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import FolderItem from './FolderItem';
import { FolderProvider, useFolderContext } from './FolderProvider';
import { FolderData } from '../../types/folder';
import { db } from '../../lib/firebase';

interface FolderTreeProps {
  folders: FolderData[];
  folderPageCounts?: Record<string, number>;
  selectedFolderId: string | null;
  onFolderSelect: (folderId: string) => void;
  onFolderMenuClick?: (e: React.MouseEvent, folderId: string) => void;
  onFolderMenuAction?: (action: string, folderId: string) => void;
  openFolderMenuId?: string | null;
  // 編集関連のprops
  newFolderName?: string;
  onNameChange?: (name: string) => void;
  onSaveName?: (folderId: string) => void;
  onKeyPress?: (e: React.KeyboardEvent, folderId: string) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  // フォルダー開閉関連
  toggleFolderOpen?: (folderId: string) => void;
  openFolderIds?: Set<string>;
  setOpenFolderIds?: React.Dispatch<React.SetStateAction<Set<string>>>;
  // 選択モード用
  showSelectionMode?: boolean;
}

const FolderTreeContent: React.FC<FolderTreeProps> = ({
  folders,
  folderPageCounts,
  selectedFolderId,
  onFolderSelect,
  onFolderMenuClick,
  onFolderMenuAction,
  openFolderMenuId,
  newFolderName,
  onNameChange,
  onSaveName,
  onKeyPress,
  inputRef,
  toggleFolderOpen,
  openFolderIds,
  setOpenFolderIds,
  showSelectionMode = false,
}) => {
  const { workspaceId } = useParams();
  const { 
    openFolderIds: contextOpenFolderIds, 
    toggleFolderOpen: contextToggleFolderOpen 
  } = useFolderContext();
  
  // ドラッグアンドドロップ用の状態
  const [isDragging, setIsDragging] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  // ドラッグセンサー設定（誤操作防止）
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px移動したらドラッグ開始
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 循環参照チェック
  const wouldCreateCircularReference = useCallback((
    draggedId: string, 
    targetId: string, 
    folders: FolderData[]
  ): boolean => {
    if (draggedId === targetId) return true;
    
    const targetFolder = folders.find(f => f.id === targetId);
    if (!targetFolder) return false;
    
    const ancestorIds = targetFolder.ancestorFolderIds || [];
    return ancestorIds.includes(draggedId);
  }, []);

  // 子フォルダーのancestorFolderIdsを更新するヘルパー関数
  const updateChildAncestors = useCallback((
    folders: FolderData[],
    parentId: string,
    newAncestorIds: string[] | null
  ): FolderData[] => {
    return folders.map(folder => {
      if (folder.parentId === parentId) {
        return {
          ...folder,
          ancestorFolderIds: newAncestorIds
        };
      }
      return folder;
    });
  }, []);

  // SidebarのtoggleFolderOpenをFolderProviderのtoggleFolderOpenに接続
  const handleToggle = useCallback((folderId: string) => {
    if (showSelectionMode) {
      // 選択モード時は外部から渡されたtoggleFolderOpenのみ使用
      toggleFolderOpen?.(folderId);
    } else {
      // 通常モード時は両方のtoggleFolderOpenを使用
      toggleFolderOpen?.(folderId);
      contextToggleFolderOpen(folderId);
    }
  }, [toggleFolderOpen, contextToggleFolderOpen, showSelectionMode]);

  // ドラッグ開始
  const handleDragStart = useCallback((event: DragStartEvent) => {
    if (showSelectionMode) return; // 選択モードではドラッグ無効
    const { active } = event;
    setIsDragging(true);
    setDraggedId(active.id as string);
  }, [showSelectionMode]);

  // ドラッグ終了
  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    if (showSelectionMode) return; // 選択モードではドラッグ無効
    const { active, over } = event;

    setIsDragging(false);
    setDraggedId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const draggedId = active.id as string;
    const targetId = over.id as string;

    // 循環参照チェック（ルートレベルへの移動は除外）
    if (targetId !== 'root' && wouldCreateCircularReference(draggedId, targetId, folders)) {
      alert(uiText("Cannot move into itself or a child folder"));
      return;
    }

    if (!workspaceId) {
      console.error('❌ [FolderTree] workspaceIdが取得できません');
      alert(uiText("Could not load workspace information"));
      return;
    }

    try {
      // Firestore batch更新
      const batch = writeBatch(db);

      const draggedFolder = folders.find(f => f.id === draggedId);
      const targetFolder = folders.find(f => f.id === targetId);

      if (!draggedFolder) {
        console.error('❌ [FolderTree] 移動対象フォルダが見つかりません');
        return;
      }

      // 新しいancestorFolderIdsを計算：親フォルダーのancestorFolderIds + 親フォルダーのID
      let newAncestorIds: string[];
      if (targetId === 'root') {
        // ルートレベルの場合は['root']のみ
        newAncestorIds = ['root'];
      } else {
        // ターゲットフォルダーを検索
        const targetFolder = folders.find(f => f.id === targetId);
        if (targetFolder) {
          // ターゲットフォルダーのancestorFolderIds + ターゲットフォルダーのID
          const targetAncestors = targetFolder.ancestorFolderIds || ['root'];
          newAncestorIds = [...targetAncestors, targetId];
        } else {
          // ターゲットフォルダーが見つからない場合はターゲットIDのみ
          newAncestorIds = [targetId];
        }
      }

      // 移動対象フォルダの更新
      const folderRef = doc(db, 'workspaces', workspaceId, 'folders', draggedId);
      batch.update(folderRef, {
        parentId: targetId,
        ancestorFolderIds: newAncestorIds,
        order: 0 // 新しい親の最後に配置
      });

      // 子フォルダーのancestorFolderIdsも更新：移動したフォルダーのancestorFolderIds + 移動したフォルダーのID
      const childFolders = folders.filter(f => f.parentId === draggedId);
      for (const childFolder of childFolders) {
        const childRef = doc(db, 'workspaces', workspaceId, 'folders', childFolder.id);
        // 移動したフォルダーのancestorFolderIds + 移動したフォルダーのID
        const childAncestorIds = [...newAncestorIds, draggedId];
        batch.update(childRef, {
          ancestorFolderIds: childAncestorIds
        });
      }

      await batch.commit();

      alert(uiText("Moved folder \"{{name}}\" to \"{{v1}}\"", { name: draggedFolder.name, v1: targetFolder?.name || (targetId === 'root' ? uiText("Root level") : uiText("Unknown")) }));
      // ローカル状態の更新はリアルタイムリスナーが自動的に行う
    } catch (error) {
      console.error('❌ [FolderTree] フォルダー移動エラー:', error);
      alert(uiText("Failed to move"));
    }
  }, [showSelectionMode, folders, wouldCreateCircularReference, workspaceId]);

  // ルートレベルのドロップゾーンコンポーネント
  const RootDropZone = () => {
    const { isOver, setNodeRef } = useDroppable({
      id: 'root',
      data: {
        type: 'root',
        isInvalid: false
      }
    });

    return (
      <div
        ref={setNodeRef}
        style={{
          minHeight: '20px',
          margin: '4px 0',
          padding: '8px',
          borderRadius: '4px',
          border: isOver 
            ? '2px dashed var(--workspace-primary, #cfae91)' 
            : '2px solid transparent',
          backgroundColor: isOver 
            ? '#f0e6d8' 
            : 'transparent',
          transition: 'all 0.2s ease',
          cursor: isOver ? 'pointer' : 'default',
          fontSize: '12px',
          color: '#666',
          textAlign: 'center',
          display: isDragging ? 'block' : 'none' // ドラッグ中のみ表示
        }}
      >
        {isOver ? uiText("Drop here to move to the root level") : uiText("Root level")}
      </div>
    );
  };

  const rootFolders = folders
    .filter(folder => folder.parentId === 'root' || !folder.parentId)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={['root', ...folders.map(f => f.id)]}
        strategy={verticalListSortingStrategy}
      >
        <div className="folder-tree">
          {(() => {
            return rootFolders.map(folder => (
              <FolderItem
                key={folder.id}
                folder={folder}
                folders={folders}
                folderPageCounts={folderPageCounts}
                level={0}
                openFolderIds={contextOpenFolderIds}
                selectedFolderId={selectedFolderId}
                onToggle={handleToggle}
                onSelect={onFolderSelect}
                onMenuClick={onFolderMenuClick}
                onMenuAction={onFolderMenuAction}
                openFolderMenuId={openFolderMenuId}
                newFolderName={newFolderName}
                onNameChange={onNameChange}
                onSaveName={onSaveName}
                onKeyPress={onKeyPress}
                inputRef={inputRef}
                isDragging={true}
                draggedId={draggedId}
                wouldCreateCircularReference={wouldCreateCircularReference}
                showSelectionMode={showSelectionMode}
              />
            ));
          })()}
          
          {/* ルートレベルのドロップゾーン */}
          <RootDropZone />
        </div>
      </SortableContext>
    </DndContext>
  );
};

const FolderTree: React.FC<FolderTreeProps> = (props) => {
  return (
    <FolderProvider 
      folders={props.folders}
      externalOpenFolderIds={props.openFolderIds}
      onOpenFolderIdsChange={props.setOpenFolderIds}
    >
      <FolderTreeContent {...props} />
    </FolderProvider>
  );
};

export default FolderTree;
